<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import BannerTop from '@/components/BusinessLogic/Home/BannerTop.vue'
import HomeKpi from '@/components/BusinessLogic/Home/HomeKpi.vue'
import HomeQuickActions from '@/components/BusinessLogic/Home/HomeQuickActions.vue'
import CallsOverTimeChart from '@/components/BusinessLogic/Charts/CallsOverTimeChart.vue'
import ErrorPage from '@/views/ErrorPage.vue'
import { useHomeDateRange } from '@/composables/home/useHomeDateRange'
import Open from '@/assets/img/svg/icons/open.svg'

const { t } = useI18n()
const route = useRoute()

const hasError = ref(false)
const errorMessage = ref<string>()
const dateRange = useHomeDateRange()

const handleKpiError = (error: Error) => {
  hasError.value = true
  errorMessage.value = error.message
}

const handleChartError = (error: Error) => {
  hasError.value = true
  errorMessage.value = error.message
}

// Get the current locale from the route
const liveSupervisionPath = computed(() => {
  const locale = route.params.locale || 'en'
  return `/${String(locale)}/live-supervision`
})
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden">
    <div class="flex flex-col flex-1 min-h-0 overflow-y-auto">
      <BannerTop v-if="!hasError" />

      <div
        class="px-10 flex flex-col space-y-6 flex-1 min-h-0 select-none"
        :class="hasError ? 'h-full' : ''"
      >
        <!-- Error State -->
        <template v-if="hasError">
          <ErrorPage class="h-full" :description="errorMessage" :show-actions="false" />
        </template>

        <!-- Normal State -->
        <template v-else>
          <!-- Title and Link Section -->
          <section aria-labelledby="realtime-overview-title">
            <div class="flex justify-between items-baseline gap-4 mb-6">
              <h2 id="realtime-overview-title" class="typography-heading-2 text-neutral-1">
                {{ t('home.realtime_overview') }}
              </h2>
              <router-link :to="liveSupervisionPath" class="typography-body-1-medium text-primary!">
                <Open class="inline-block w-4 h-4 mr-1" aria-hidden="true" focusable="false" />
                {{ t('home.go_to_live_supervision') }}
              </router-link>
            </div>

            <!-- KPI Widgets -->
            <HomeKpi @error="handleKpiError" />
          </section>

          <!-- Activity Insights Section -->
          <section aria-labelledby="activity-chart-title" class="flex-1 min-h-92">
            <CallsOverTimeChart
              v-if="dateRange"
              class="w-full h-full"
              :dateRange="dateRange"
              @error="handleChartError"
            />
          </section>

          <!-- Quick Actions Section -->
          <div class="pb-10">
            <HomeQuickActions />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
